import React, { useState } from 'react';
import { InterventionAreas } from '../../types/policyImpact';
import PolicyLeverControl from './PolicyLeverControl';
import './InterventionAreasControl.css';

interface SubPolicyValues {
  transitOrientedDevelopment: number;
  activeTransportation: number;
  transitImprovements: number;
  parkingPricing: number;
  micromobility: number;
  otherInitiatives: number; // User-adjustable
}

interface InterventionAreasControlProps {
  interventionAreas: InterventionAreas;
  onChange: (updatedAreas: InterventionAreas) => void;
  onExpandedChange?: (expanded: keyof InterventionAreas | null) => void;
  subPolicyValues?: SubPolicyValues;
  onSubPolicyChange?: (subPolicyValues: SubPolicyValues) => void;
  onSubPolicyExpand?: (key: string | null) => void;
  parkingVMTReductionPercent?: number; // Calculated parking pricing VMT reduction %
  micromobilityVMTReductionPercent?: number; // Calculated micromobility VMT reduction %
}

interface InterventionData {
  key: keyof InterventionAreas;
  label: string;
  description: string;
  min: number;
  max: number;
  color: string;
  bgColor: string;
}

const interventions: InterventionData[] = [
  // Transportation - Light Blue
  { key: 'vmtReduction', label: 'VMT Reduction', description: 'Reduce vehicle miles traveled through transit, biking, and telework', min: 0, max: 50, color: '#bfdbfe', bgColor: '#eff6ff' },
  { key: 'vehicleElectrification', label: 'Vehicle Electrification', description: 'Percentage of vehicles that will be electric by 2045', min: 0, max: 80, color: '#bfdbfe', bgColor: '#eff6ff' },
  // Buildings - Yellow
  { key: 'energyEfficiency', label: 'Energy Efficiency', description: 'Improve building energy efficiency through retrofits and standards', min: 0, max: 100, color: '#fef08a', bgColor: '#fefce8' },
  { key: 'buildingsElectrification', label: 'Building Electrification', description: 'Replace fossil fuel heating/cooking with electric alternatives', min: 0, max: 100, color: '#fef08a', bgColor: '#fefce8' },
  { key: 'gridDecarbonization', label: 'Grid Decarbonization', description: 'Increase renewable energy capacity to decarbonize electricity grid', min: 0, max: 100, color: '#fef08a', bgColor: '#fefce8' },
  // Other - Orange
  { key: 'organicsDiversion', label: 'Organics Diversion', description: 'Divert organic waste from landfills to reduce methane emissions', min: 0, max: 100, color: '#fed7aa', bgColor: '#fff7ed' },
];

interface VMTSubPolicy {
  key: string;
  label: string;
  value: number;
  min: number;
  max: number;
}

const InterventionAreasControl: React.FC<InterventionAreasControlProps> = ({
  interventionAreas,
  onChange,
  onExpandedChange,
  subPolicyValues = { transitOrientedDevelopment: 0, activeTransportation: 0, transitImprovements: 0, parkingPricing: 0, micromobility: 0, otherInitiatives: 0 },
  onSubPolicyChange,
  onSubPolicyExpand,
  parkingVMTReductionPercent = 0,
  micromobilityVMTReductionPercent = 0,
}) => {
  const [expandedIntervention, setExpandedIntervention] = useState<keyof InterventionAreas | null>(null);
  const [expandedSubPolicy, setExpandedSubPolicy] = useState<string | null>(null);
  
  // Calculate sum of policy reductions (excluding Other Initiatives)
  const sumOfPolicyReductions = 
    (subPolicyValues.transitOrientedDevelopment || 0) +
    (subPolicyValues.activeTransportation || 0) +
    (subPolicyValues.transitImprovements || 0) +
    parkingVMTReductionPercent +
    micromobilityVMTReductionPercent;
  
  // Other Initiatives is now user-adjustable, stored in subPolicyValues
  const otherInitiativesValue = subPolicyValues.otherInitiatives || 0;
  
  // VMT Reduction sub-policies
  // Note: For parking pricing and micromobility, the slider controls the policy level (price increase % or scale %),
  // not the resulting VMT reduction percentage. The VMT reduction is calculated separately.
  const vmtSubPolicies = [
    { key: 'transitOrientedDevelopment', label: 'Transit-Oriented Development', min: 0, max: 15, value: subPolicyValues.transitOrientedDevelopment || 0 },
    { key: 'activeTransportation', label: 'Expand active transportation infrastructure by', min: 0, max: 10, value: subPolicyValues.activeTransportation || 0 },
    { key: 'transitImprovements', label: 'Expand transit service by', min: 0, max: 50, value: subPolicyValues.transitImprovements || 0 },
    { key: 'parkingPricing', label: 'Increase parking price by', min: 0, max: 300, value: subPolicyValues.parkingPricing || 0 },
    { key: 'micromobility', label: 'Expand micromobility by', min: 0, max: 200, value: subPolicyValues.micromobility || 0 },
    { key: 'otherInitiatives', label: '% Reduction Other Initiatives', min: 0, max: 50, value: otherInitiativesValue },
  ];

  const handleSubPolicyValueChange = (key: string, value: number) => {
    if (onSubPolicyChange) {
      const updatedValues = {
        ...subPolicyValues,
        [key as keyof SubPolicyValues]: value,
      };
      onSubPolicyChange(updatedValues);
      
      // If Other Initiatives changes, update total VMT reduction
      if (key === 'otherInitiatives') {
        const newTotalVmtReduction = sumOfPolicyReductions + value;
        onChange({
          ...interventionAreas,
          vmtReduction: Math.max(0, Math.min(50, newTotalVmtReduction)),
        });
      } else {
        // When other sub-policies change, recalculate total VMT reduction
        // Note: parkingVMTReductionPercent and micromobilityVMTReductionPercent are calculated
        // separately and will be included in the sum when they update
        // The total should be sum of all sub-policies including Other Initiatives
        const updatedSumOfPolicyReductions = 
          (key === 'transitOrientedDevelopment' ? value : (subPolicyValues.transitOrientedDevelopment || 0)) +
          (key === 'activeTransportation' ? value : (subPolicyValues.activeTransportation || 0)) +
          (key === 'transitImprovements' ? value : (subPolicyValues.transitImprovements || 0)) +
          parkingVMTReductionPercent +
          micromobilityVMTReductionPercent;
        
        const newTotalVmtReduction = updatedSumOfPolicyReductions + otherInitiativesValue;
        onChange({
          ...interventionAreas,
          vmtReduction: Math.max(0, Math.min(50, newTotalVmtReduction)),
        });
      }
    }
  };
  
  const handleExpandClick = (key: keyof InterventionAreas) => {
    const newExpanded = expandedIntervention === key ? null : key;
    setExpandedIntervention(newExpanded);
    if (onExpandedChange) {
      onExpandedChange(newExpanded);
    }
    // Scroll to details section for vehicle electrification
    if (newExpanded === 'vehicleElectrification') {
      setTimeout(() => {
        const element = document.getElementById('vehicle-electrification-details');
        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  const handleSubPolicyClick = (key: string) => {
    const newKey = expandedSubPolicy === key ? null : key;
    setExpandedSubPolicy(newKey);
    if (onSubPolicyExpand) onSubPolicyExpand(newKey);
    if (onExpandedChange) onExpandedChange(newKey ? 'vmtReduction' : null);
    if (newKey && (newKey === 'parkingPricing' || newKey === 'micromobility' || newKey === 'transitImprovements')) {
      setTimeout(() => {
        const element = document.getElementById(
          newKey === 'parkingPricing'
            ? 'parking-pricing-details'
            : newKey === 'micromobility'
            ? 'micromobility-details'
            : 'transit-service-details'
        );
        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  const handleChange = (key: keyof InterventionAreas, value: number) => {
    onChange({
      ...interventionAreas,
      [key]: value,
    });
    
    // If VMT Reduction changed, update Other Initiatives to balance
    if (key === 'vmtReduction' && onSubPolicyChange) {
      const sumOfPolicyReductions = 
        (subPolicyValues.transitOrientedDevelopment || 0) +
        (subPolicyValues.activeTransportation || 0) +
        (subPolicyValues.transitImprovements || 0) +
        parkingVMTReductionPercent +
        micromobilityVMTReductionPercent;
      
      const calculatedOtherInitiatives = Math.max(0, value - sumOfPolicyReductions);
      
      onSubPolicyChange({
        ...subPolicyValues,
        otherInitiatives: calculatedOtherInitiatives,
      });
    }
  };

  return (
    <div className="intervention-areas-control">
      <h3 className="intervention-areas-title">Implementation by 2045</h3>
      <p className="intervention-areas-description">
        Adjust each intervention to set the extent of implementation by 2045
      </p>

      <div className="intervention-table">
        {interventions.map((intervention) => (
          <React.Fragment key={intervention.key}>
            <div 
              className="intervention-table-row"
              style={{ backgroundColor: intervention.bgColor, borderLeftColor: intervention.color }}
            >
            <div className="intervention-table-label-cell">
              <button
                className="intervention-info-button-small"
                onClick={() => handleExpandClick(intervention.key)}
                title={intervention.description}
              >
                {expandedIntervention === intervention.key ? '−' : '+'}
              </button>
              <span className="intervention-item-label-compact">{intervention.label}</span>
            </div>
            <div className="intervention-table-slider-cell">
              <div className="compact-slider-wrapper">
                <input
                  type="range"
                  min={intervention.min}
                  max={intervention.max}
                  step={5}
                  value={interventionAreas[intervention.key]}
                  onChange={(e) => handleChange(intervention.key, Number(e.target.value))}
                  className="compact-slider"
                  style={{
                    background: `linear-gradient(to right, ${intervention.color} 0%, ${intervention.color} ${((interventionAreas[intervention.key] - intervention.min) / (intervention.max - intervention.min)) * 100}%, #e5e7eb ${((interventionAreas[intervention.key] - intervention.min) / (intervention.max - intervention.min)) * 100}%, #e5e7eb 100%)`,
                    '--thumb-border-color': intervention.color === '#bfdbfe' ? '#3b82f6' : intervention.color === '#fef08a' ? '#f59e0b' : '#ea580c',
                  } as React.CSSProperties}
                />
                <div className="compact-slider-value">
                  {interventionAreas[intervention.key]}%
                </div>
              </div>
            </div>
          </div>

          {/* VMT Reduction Sub-policies */}
          {intervention.key === 'vmtReduction' && expandedIntervention === 'vmtReduction' && (
            <>
              {vmtSubPolicies.map((subPolicy) => (
                <div 
                  key={subPolicy.key}
                  className="intervention-sub-policy-row"
                  style={{ 
                    backgroundColor: '#dbeafe',
                    borderLeftColor: '#3b82f6' 
                  }}
                >
                  <div className="intervention-table-label-cell">
                    <button
                      className="intervention-info-button-small"
                      onClick={() => handleSubPolicyClick(subPolicy.key)}
                      title="Click to expand details"
                    >
                      {expandedSubPolicy === subPolicy.key ? '−' : '+'}
                    </button>
                    <span className="intervention-item-label-compact sub-policy-indent">{subPolicy.label}</span>
                  </div>
                  <div className="intervention-table-slider-cell">
                    <div className="compact-slider-wrapper">
                      <input
                        type="range"
                        min={subPolicy.min}
                        max={subPolicy.max}
                        step={subPolicy.key === 'parkingPricing' ? 10 : 1}
                        value={subPolicy.value ?? (subPolicyValues[subPolicy.key as keyof SubPolicyValues] || 0)}
                        onChange={(e) => {
                          handleSubPolicyValueChange(subPolicy.key, Number(e.target.value));
                        }}
                        className="compact-slider"
                        style={{
                          background: `linear-gradient(to right, #bfdbfe 0%, #bfdbfe ${((subPolicy.value ?? (subPolicyValues[subPolicy.key as keyof SubPolicyValues] || 0)) / subPolicy.max) * 100}%, #e5e7eb ${((subPolicy.value ?? (subPolicyValues[subPolicy.key as keyof SubPolicyValues] || 0)) / subPolicy.max) * 100}%, #e5e7eb 100%)`,
                          '--thumb-border-color': '#3b82f6',
                        } as React.CSSProperties}
                      />
                      <div className="compact-slider-value">
                        {(subPolicy.value ?? (subPolicyValues[subPolicy.key as keyof SubPolicyValues] || 0)).toFixed(1)}%
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default InterventionAreasControl;
